import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  lessons: [],
  currentLesson: null,
  isLoading: false,
  error: null,
  filters: {
    search: '',
    courseId: '',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  },
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  },
};

const lessonSlice = createSlice({
  name: 'lesson',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentLesson: (state) => {
      state.currentLesson = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setCurrentPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setItemsPerPage: (state, action) => {
      state.pagination.limit = action.payload;
      state.pagination.page = 1; // Reset to first page when changing page size
    },
    resetPagination: (state) => {
      state.pagination = {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Lessons with Filters
      .addCase('lesson/fetchLessonsWithFilters/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('lesson/fetchLessonsWithFilters/fulfilled', (state, action) => {
        state.isLoading = false;
        state.lessons = action.payload.data || action.payload;
        state.pagination = action.payload.pagination || state.pagination;
        state.error = null;
      })
      .addCase('lesson/fetchLessonsWithFilters/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch lessons';
      })
      // Get Lesson by ID
      .addCase('lesson/getLessonById/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('lesson/getLessonById/fulfilled', (state, action) => {
        state.isLoading = false;
        state.currentLesson = action.payload;
        state.error = null;
      })
      .addCase('lesson/getLessonById/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to fetch lesson';
      })
      // Create Lesson
      .addCase('lesson/createLesson/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('lesson/createLesson/fulfilled', (state, action) => {
        state.isLoading = false;
        state.lessons.unshift(action.payload);
        state.error = null;
      })
      .addCase('lesson/createLesson/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to create lesson';
      })
      // Update Lesson
      .addCase('lesson/updateLesson/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('lesson/updateLesson/fulfilled', (state, action) => {
        state.isLoading = false;
        const index = state.lessons.findIndex(lesson => lesson._id === action.payload._id);
        if (index !== -1) {
          state.lessons[index] = action.payload;
        }
        state.error = null;
      })
      .addCase('lesson/updateLesson/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to update lesson';
      })
      // Delete Lesson
      .addCase('lesson/deleteLesson/pending', (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase('lesson/deleteLesson/fulfilled', (state, action) => {
        state.isLoading = false;
        state.lessons = state.lessons.filter(lesson => lesson._id !== action.payload);
        state.error = null;
      })
      .addCase('lesson/deleteLesson/rejected', (state, action) => {
        state.isLoading = false;
        state.error = action.payload || 'Failed to delete lesson';
      });
  },
});

export const { 
  clearError, 
  clearCurrentLesson, 
  setFilters, 
  setPagination, 
  setCurrentPage, 
  setItemsPerPage, 
  resetPagination 
} = lessonSlice.actions;

export default lessonSlice.reducer;

